import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import TarifRechner from "@/components/TarifRechner";
import SwissBadge from "@/components/SwissBadge";

// Aligned 1:1 mit gutachter-landing.preview.emergentagent.com/pricing
const PKG_META = {
  flexibel: {
    tagline: "Pay-per-Gutachten mit kleiner Grundgebühr — ideal für Einsteiger und Teilzeit-Sachverständige.",
    perCaseLine: "+ 75 € pro erstelltem Gutachten — egal wie viele",
    features: [
      "85 € monatliche Grundgebühr",
      "75 € pro Gutachten — fester Preis, ohne Staffel",
      "Voller Funktionsumfang",
      "Mobile Erfassung & PDF-Export",
      "Auto-Schadenserkennung",
      "E-Mail Support",
      "Alle 3 Monate kündbar",
    ],
    cta: "Flexibel starten",
  },
  flatrate: {
    tagline: "Unbegrenzte Gutachten — für Büros, Teams und Vielschreiber.",
    perCaseLine: "+ 10 € Nutzungsgebühr pro erstelltem Gutachten",
    highlight: true,
    badge: "Beste Wahl ab ~12 Gutachten/Mt.",
    features: [
      "Unbegrenzte Anzahl Gutachten",
      "850 € Grundgebühr pro Monat",
      "Nur 10 € Nutzungsgebühr pro Gutachten",
      "Auto-Schadenserkennung (Gemini Vision)",
      "Sprachaufnahme & Auto-Transkription",
      "Backoffice-Workflow & Rollen",
      "DAT + Versicherungsabfrage",
      "Priorisierter Support",
      "Alle 3 Monate kündbar",
    ],
    cta: "Flatrate wählen",
  },
};

const FAQ = [
  {
    q: "Was ist ein Provisionspartner?",
    a: "Für alle, die (noch) nicht selbstständig sind: Du reichst nur Kundendaten und Schadenfotos über die App ein — unser Team erstellt das Gutachten im eigenen Namen. Du erhältst 200 € Provision pro Auftrag nach Zahlungseingang der Versicherung. Komplett kostenlos, keine Software-Gebühren.",
  },
  {
    q: "Wann lohnt sich Flatrate statt Flexibel?",
    a: "Ab rund 12 Gutachten pro Monat ist die Flatrate günstiger. Bei 10 Gutachten zahlst du Flexibel 835 € (85 + 10 × 75) · Flatrate 950 € (850 + 10 × 10). Ab 12 Gutachten dreht sich das um. Nutze den Rechner oben — er zeigt dir exakt, wann welcher Tarif passt.",
  },
  {
    q: "Warum gibt es bei Flexibel eine Grundgebühr?",
    a: "Die monatliche Grundgebühr (85 €) deckt Hosting, OCR-Modelle, DSGVO-Infrastruktur und Support — auch wenn du in einem Monat keine Gutachten erstellst. So bleibt das Tool dauerhaft einsatzbereit, statt nur bei aktiver Nutzung.",
  },
  {
    q: "Kann ich zwischen Flexibel und Flatrate wechseln?",
    a: "Jederzeit. Upgrade auf Flatrate greift sofort, Downgrade auf Flexibel zum Ende der laufenden Periode. Keine Vertragsbindung, keine Gebühren für den Wechsel.",
  },
  {
    q: "Ist die Testphase wirklich kostenlos?",
    a: "Ja — 2 Tage kostenlos testen, keine Kreditkarte erforderlich. Wenn du innerhalb der 2 Tage kein Abo wählst, wird dein Account automatisch deaktiviert (eine Reaktivierung ist über den Support möglich).",
  },
  {
    q: "Kann ich jederzeit kündigen?",
    a: "Beide Tarife sind alle 3 Monate kündbar. Keine Mindestlaufzeit darüber hinaus, keine Einrichtungsgebühr, keine versteckten Kosten.",
  },
  {
    q: "Wie wird bei Flexibel abgerechnet?",
    a: "Monatlich im Nachgang: Grundgebühr 85 € fest plus 75 € pro erstelltem Gutachten — kein Volumen-Sprung, ein einheitlicher Preis.",
  },
  {
    q: "Was passiert mit meinen Daten nach Kündigung?",
    a: "Du erhältst einen Daten-Export (PDF + JSON). 30 Tage später werden alle personenbezogenen Daten DSGVO-konform gelöscht.",
  },
];

export default function Pricing() {
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/billing/packages").then(({ data }) => setPackages(data.packages));
  }, []);

  // Onboarding-Status laden, damit wir wissen ob wir den schnellen
  // „Plan markieren"-Flow oder den vollen Stripe-Checkout starten müssen.
  useEffect(() => {
    if (!user) return;
    let stopped = false;
    Promise.resolve().then(async () => {
      try {
        const { data } = await api.get("/onboarding/status");
        if (!stopped) setOnboardingStatus(data);
      } catch { /* silent */ }
    });
    return () => { stopped = true; };
  }, [user]);

  const selectOnboardingPlan = async (plan, pkgId) => {
    setLoading(pkgId);
    try {
      await api.post("/onboarding/select-plan", { plan });
      toast.success(`${plan === "flexibel" ? "Flexibel" : "Flatrate"} ausgewählt — als Nächstes: Profil vervollständigen.`);
      window.dispatchEvent(new Event("onboarding:refresh"));
      navigate("/settings?tab=profile");
    } catch (e) {
      toast.error(apiError(e, "Plan konnte nicht gespeichert werden."));
    } finally {
      setLoading(null);
    }
  };

  const startCheckout = async (pkgId) => {
    if (!user) { navigate("/register"); return; }
    // Im Onboarding (Plan noch nicht gewählt) → Plan einfach markieren,
    // statt Stripe-Checkout zu starten. Bezahlung kommt nach SEPA-Mandat.
    if (onboardingStatus && onboardingStatus.applicable && !onboardingStatus.plan_selected) {
      const planMap = { pkg_pay_per_use: "flexibel", pkg_flatrate: "flatrate" };
      const plan = planMap[pkgId];
      if (plan) {
        await selectOnboardingPlan(plan, pkgId);
        return;
      }
    }
    setLoading(pkgId);
    await api
      .post("/billing/checkout", {
        package_id: pkgId,
        origin_url: window.location.origin,
      })
      .then((res) => {
        // Success: Stripe-Checkout-URL → direkter Redirect (kein Reset
        // nötig, Browser lädt eh neu).
        if (res?.data?.url) {
          window.location.href = res.data.url;
        } else {
          setLoading(null);
          toast.error("Checkout-URL fehlt.");
        }
      })
      .catch((err) => {
        setLoading(null);
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Checkout fehlgeschlagen");
      });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="pricing-page">
      <header className="border-b border-zinc-200 bg-white/85 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="pricing-logo">
            <img src="/easy-gutachter-logo.png" alt="Easy Gutachter" className="h-12 w-auto select-none" draggable={false} />
          </Link>
          {user ? (
            <Link to="/dashboard"><Button size="sm" variant="outline">Dashboard</Button></Link>
          ) : (
            <div className="flex gap-2">
              <Link to="/login"><Button size="sm" variant="ghost">Anmelden</Button></Link>
              <Link to="/register"><Button size="sm" className="brand-gradient-bg text-white">Kostenlos starten</Button></Link>
            </div>
          )}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
        {/* Hero */}
        <div className="text-center max-w-2xl mx-auto">
          <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200">
            Transparente Preise
          </Badge>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-4">
            Zwei Modelle.<br />Keine versteckten Kosten.
          </h1>
          <p className="text-zinc-600 mt-5 text-lg">
            Pay-per-Gutachten oder Flatrate — du entscheidest je nach Volumen.
            2 Tage kostenlos testen. Alle 3 Monate kündbar.
          </p>
        </div>

        {/* Tarif-Rechner — prominent über den Tarif-Karten,
            damit der User direkt sieht, welcher Plan zu seinem Volumen passt. */}
        <div className="mt-14 scroll-mt-20" id="rechner">
          <TarifRechner />
        </div>

        {/* Pricing cards */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mt-14 max-w-4xl mx-auto" id="vergleich">
          {Object.entries(packages).map(([id, pkg]) => {
            const meta = PKG_META[id] || {};
            const isHighlight = meta.highlight;
            return (
              <div
                key={id}
                className={`relative rounded-3xl border p-7 sm:p-9 bg-white transition-all flex flex-col ${
                  isHighlight ? "border-cyan-300 shadow-xl shadow-cyan-100/60 lg:-translate-y-1" : "border-zinc-200 hover:border-zinc-300 hover:shadow-md"
                }`}
                data-testid={`pricing-${id}`}
              >
                {meta.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap">
                    <Badge className="brand-gradient-bg text-white border-0 px-3 shadow-md">
                      {meta.badge}
                    </Badge>
                  </div>
                )}
                <div className="text-2xl font-display font-bold text-zinc-900">{pkg.name}</div>
                <div className="flex items-baseline gap-1 mt-3">
                  <span className="font-display text-5xl font-bold">{pkg.amount.toFixed(0)}</span>
                  <span className="text-2xl font-semibold text-zinc-500">€</span>
                  <span className="text-sm text-zinc-500 ml-1">/ {pkg.interval}</span>
                </div>
                <p className="text-sm font-semibold text-cyan-700 mt-2">{meta.perCaseLine}</p>
                <p className="text-sm text-zinc-600 mt-3 leading-relaxed">{meta.tagline}</p>
                <Button
                  className={`w-full mt-6 ${isHighlight ? "brand-gradient-bg text-white" : ""}`}
                  variant={isHighlight ? "default" : "outline"}
                  onClick={() => startCheckout(id)}
                  disabled={loading === id}
                  data-testid={`pricing-cta-${id}`}
                >
                  {loading === id ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <ArrowRight className="h-4 w-4 mr-2" />
                  )}
                  {meta.cta || "Plan wählen"}
                </Button>
                <ul className="space-y-2.5 mt-7 text-sm">
                  {(meta.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                      <span className="text-zinc-700">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Provisionspartner-Info */}
        <div
          className="mt-10 max-w-4xl mx-auto rounded-3xl border border-emerald-200 bg-emerald-50/50 p-7 sm:p-9"
          id="provisionspartner"
          data-testid="pricing-provision-info"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1">
              <Badge variant="outline" className="bg-white text-emerald-700 border-emerald-300">
                0 € Software-Kosten
              </Badge>
              <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-3">
                Nicht selbstständig? Werde Provisionspartner.
              </h3>
              <p className="text-zinc-700 mt-3 leading-relaxed">
                Du reichst nur Kundendaten + Schadenfotos ein — unser Team erstellt das
                Gutachten. Dafür bekommst du <strong>200 € Provision pro Auftrag</strong>,
                ausgezahlt nach Zahlungseingang der Versicherung. 100 % kostenlos, keine
                Grundgebühr, keine Verpflichtung.
              </p>
              <ul className="mt-4 space-y-2 text-sm">
                {[
                  "200 € Provision pro eingereichtem Auftrag",
                  "Keine Software- oder Abo-Kosten",
                  "Nur Fotos + Kundendaten hochladen — wir erledigen den Rest",
                  "Auszahlung direkt auf dein Konto",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                    <span className="text-zinc-700">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="md:w-56 shrink-0 text-center">
              <div className="font-display text-5xl font-bold text-emerald-700">200 €</div>
              <div className="text-sm text-zinc-500 mt-1">pro Auftrag</div>
              <Link to="/register">
                <Button className="w-full mt-5 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="pricing-provision-cta">
                  <ArrowRight className="h-4 w-4 mr-2" /> Kostenlos Partner werden
                </Button>
              </Link>
              <Link to="/provisionspartner" className="block mt-3 text-sm text-emerald-700 font-semibold hover:underline" data-testid="pricing-provision-more-link">
                Mehr erfahren →
              </Link>
            </div>
          </div>
        </div>


        {/* FAQ */}
        <div className="mt-20 sm:mt-28 max-w-3xl mx-auto" id="faq">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-center">
            Häufige Fragen.
          </h2>
          <div className="mt-8 space-y-3">
            {FAQ.map((item, i) => (
              <details
                key={i}
                className="group rounded-2xl border border-zinc-200 bg-white open:border-cyan-200 open:bg-cyan-50/30 transition"
                data-testid={`faq-item-${i}`}
              >
                <summary className="flex items-center justify-between cursor-pointer px-5 sm:px-6 py-4 list-none select-none">
                  <span className="font-display font-semibold text-zinc-900 pr-4">{item.q}</span>
                  <span className="h-7 w-7 rounded-full bg-zinc-100 group-open:bg-cyan-600 group-open:text-white flex items-center justify-center text-xl font-light shrink-0 transition">
                    <span className="group-open:hidden">+</span>
                    <span className="hidden group-open:inline">−</span>
                  </span>
                </summary>
                <div className="px-5 sm:px-6 pb-5 text-zinc-700 leading-relaxed">
                  {item.a}
                </div>
              </details>
            ))}
          </div>
        </div>

        <div className="flex justify-center pb-12">
          <SwissBadge />
        </div>
      </div>
    </div>
  );
}
